// Home — "What are you craving?". Answers "what should I cook right now?" first; the date-ordered
// list is one tap away (Just saved → All by date). One /home call paints the whole screen.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link } from '../router';
import { Chat, Plus, SearchIcon, VegMark } from '../icons';
import { Avatar, BottomNav, ErrorBox, SectionHead, useToast, Wordmark } from '../components/Shell';
import { BookCover, MoodTile, SurpriseBox } from '../components/Covers';
import { AddReelSheet } from '../components/AddReelSheet';
import { SwipeToGrocery } from '../components/Swipe';
import { reelThumb, Thumb } from '../components/Thumb';
import { CATALOG } from '../albums';
import { namesFor } from '../lib/format';
import { shortDateL, useT } from '../i18n';
import { useScript } from '../script';
import { load, save, KEYS } from '../storage';
import type { HomeView, Profile, SavedReel, Script } from '../types';

const CACHE = 'er.cache.home';
const DM_URL = 'https://ig.me/m/eat.recipe';

export function Home() {
  const t = useT();
  const [script] = useScript();
  const [home, setHome] = useState<HomeView | null>(() => load<HomeView | null>(CACHE, null));
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [toast, showToast] = useToast();
  const me = load<Profile | null>(KEYS.me, null);

  const refresh = () =>
    api.home().then((h) => { setHome(h); save(CACHE, h); setErr(''); }).catch((e: ApiError) => {
      if (e.status !== 401) setErr(e.message);
    });
  useEffect(() => { refresh(); }, []);
  // Poll while a reel is being read, so it moves from "Just saved" into its albums without a reload.
  const reading = home?.just_saved.some((r) => r.status === 'queued' || r.status === 'processing');
  useEffect(() => {
    if (!reading) return;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [reading]);

  const first = (me?.name || me?.username || '').split(/\s+/)[0];
  const name = first ? `, ${first[0].toUpperCase()}${first.slice(1)}` : '';
  const isNew = home !== null && home.total === 0;

  return (
    <div class="page with-nav" style="padding-bottom:calc(168px + env(safe-area-inset-bottom))">
      <header class="topbar"><Wordmark /><Avatar /></header>

      <section style="padding:10px 20px 0;display:flex;flex-direction:column;gap:4px">
        {home && <div style="font-size:14px;font-weight:600;color:var(--accent-text)">{t(`greet.${home.bucket}`, { name })}</div>}
        <h1 class="h1" style="font-size:34px;line-height:1.1">{t('home.title')}</h1>
      </section>

      <Link href="/search" class="search-link">
        <SearchIcon style="flex:none;color:var(--ink)" />
        <span style="overflow:hidden;text-overflow:ellipsis">{home?.total ? t('home.search', { n: home.total }) : t('home.searchEmpty')}</span>
      </Link>

      {err && !home && <ErrorBox message={err} onRetry={refresh} />}
      {!home && !err && (
        <div class="pad" style="padding-top:22px;display:grid;grid-template-columns:1fr 1fr;gap:10px" aria-label={t('common.loading')}>
          {[1, 2, 3, 4].map(() => <div class="skeleton" style="height:118px;border-radius:20px" />)}
        </div>
      )}

      {isNew && (
        <section class="card" style="margin:22px 16px 0;padding:18px;display:flex;flex-direction:column;gap:12px">
          <div class="display" style="font-weight:700;font-size:20px;line-height:1.3">{t('home.newTitle')}</div>
          <div class="muted" style="line-height:1.6">{t('home.newBody')}</div>
          <a class="btn btn-primary" href={DM_URL}><Chat />{t('home.openInstagram')}</a>
          <button class="btn-text" style="color:var(--accent-text)" onClick={() => setAdding(true)}>{t('home.pasteLink')}</button>
        </section>
      )}

      {home && home.moods.length > 0 && (
        <section style="padding:22px 16px 0">
          <h2 class="h2" style="font-size:20px;margin:0 0 10px 4px">{t('home.mood')}</h2>
          <div class="mood-grid">{home.moods.map((c) => <MoodTile key={c.ref} c={c} />)}</div>
        </section>
      )}

      {home?.can_surprise && (
        <section style="padding:14px 16px 0"><SurpriseBox label={t('home.surprise')} /></section>
      )}

      {home && home.cook_again.length > 0 && (
        <section style="padding-top:26px">
          <SectionHead title={t('home.cookAgain')} hint={t('home.cookAgainHint')} />
          <div class="hrow">{home.cook_again.map((r) => <AgainCard key={r.id} r={r} script={script} />)}</div>
        </section>
      )}

      {home && home.albums.length > 0 && (
        <section style="padding-top:26px">
          <SectionHead title={t('home.yourAlbums')} link={{ href: '/albums', label: t('common.seeAll') }} />
          <div class="hrow">{home.albums.map((c) => <BookCover key={c.ref} c={c} size="row" />)}</div>
        </section>
      )}

      {home && home.just_saved.length > 0 && (
        <section style="padding-top:26px">
          <SectionHead title={t('home.justSaved')} link={{ href: '/timeline', label: t('home.allByDate') }} />
          <div class="rows" style="margin:0 16px">{home.just_saved.map((r) => r.status === 'ready' && r.ingredient_count > 0
            ? <SwipeToGrocery key={r.id} id={r.id} onToast={showToast}><JustSavedRow r={r} script={script} /></SwipeToGrocery>
            : <JustSavedRow key={r.id} r={r} script={script} />)}</div>
        </section>
      )}

      <button class="fab" onClick={() => setAdding(true)}><Plus />{t('common.addReel')}</button>
      {adding && <AddReelSheet onClose={() => setAdding(false)} onAdded={() => { setAdding(false); refresh(); showToast(t('add.added')); }} />}
      {toast}
      <BottomNav current="home" />
    </div>
  );
}

function AgainCard({ r, script }: { r: SavedReel; script: Script }) {
  const t = useT();
  const [primary, secondary] = namesFor({ ta: r.dish_name_ta, en: r.dish_name_en, tg: r.dish_name_translit }, script);
  return (
    <Link href={`/r/${r.id}`} class="mini-card">
      <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="top" />
      <span class="ta-name clamp2" style="font-weight:700;font-size:18px" lang={script === 'ta' ? 'ta' : undefined}>{primary}</span>
      {secondary && <span class="clamp2" style="font-size:14px;color:var(--muted)">{secondary}</span>}
      <span class="pill" style="margin-top:6px;align-self:flex-start">
        {r.last_cooked ? t('n.cookedOn', { n: r.cooked_count, date: shortDateL(r.last_cooked) }) : t('n.cooked', { n: r.cooked_count })}
      </span>
    </Link>
  );
}

/** Which albums a new recipe joined, in the reader's script: "Kerala · Comfort food". */
function joinedAlbums(r: SavedReel, script: Script): string {
  const pick = (n?: { en: string; ta: string; tg: string }) => (n ? (script === 'ta' ? n.ta : script === 'tg' ? n.tg : n.en) : '');
  return [pick(r.region ? CATALOG.region[r.region] : undefined), ...(r.moods ?? []).slice(0, 2).map((m) => pick(CATALOG.craving[m]))]
    .filter(Boolean).join(' · ');
}

function JustSavedRow({ r, script }: { r: SavedReel; script: Script }) {
  const t = useT();
  if (r.status === 'queued' || r.status === 'processing') {
    return (
      <div class="row soft" role="status">
        <span class="dot" aria-hidden="true" />
        <span class="grow">
          <span style="font-size:15px;font-weight:700">{r.creator_handle ? t('home.readingFrom', { who: '@' + r.creator_handle }) : t('home.reading')}</span>
          <span class="sub">{t('home.landsInAlbums')}</span>
        </span>
      </div>
    );
  }
  const [primary] = namesFor({ ta: r.dish_name_ta, en: r.dish_name_en, tg: r.dish_name_translit }, script);
  const partial = r.status === 'ready' && r.ingredient_count === 0 && r.step_count === 0;
  const albums = joinedAlbums(r, script);
  const sub = r.status === 'failed' ? t('home.failed') : partial ? t('home.noRecipe') : albums ? t('home.addedTo', { albums }) : '';
  return (
    <Link href={`/r/${r.id}`} class="row">
      <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="row" />
      <span class="grow">
        <span class="ta-name clamp2" style="font-size:16px;font-weight:700;display:flex;gap:6px;align-items:center" lang={script === 'ta' ? 'ta' : undefined}>
          <VegMark veg={r.is_veg} size={12} />
          {r.status === 'failed' && !r.dish_name_en ? (r.creator_handle ? '@' + r.creator_handle : t('card.failed')) : primary}
        </span>
        {sub && <span class="sub">{sub}</span>}
      </span>
    </Link>
  );
}
